/**
 * Hexagons API
 * Fetches H3 hexagon data for map display
 */

import { cellToBoundary } from 'h3-js';
import { apiClient } from './apiClient';

export interface NearbyHexagon {
  h3Index: string;
  latitude: number;
  longitude: number;
  distance?: number;
  isAvailable?: boolean;
}

export interface HexagonData {
  _id?: string;
  h3Index: string;
  centerLat: number;
  centerLon: number;
  boundary: number[][];
  isAvailable?: boolean;
  claimedForSubcategory?: boolean;
  claimedForSubcategories?: string[];
  claimedBy?: { partnerName: string };
}

interface HexagonsApiResponse {
  success: boolean;
  count?: number;
  hexagons: HexagonData[];
}

export interface NearbyHexagonsApiResponse {
  success: boolean;
  data?: NearbyHexagon[];
  hexagons?: NearbyHexagon[];
}

/**
 * Fetch all hexagons for map display.
 * When subcategories are provided, returned hexagons include claimedForSubcategory (union of all).
 */
export async function getAllHexagons(
  subcategories?: string[],
  category?: string,
  excludeFormId?: string,
  excludePartnerId?: string
): Promise<HexagonData[]> {
  const params: Record<string, string> = {};
  if (subcategories && subcategories.length > 0) {
    params.subcategories = subcategories.join(',');
  }
  if (category) {
    params.category = category;
  }
  if (excludeFormId) {
    params.excludeFormId = excludeFormId;
  }
  if (excludePartnerId) {
    params.excludePartnerId = excludePartnerId;
  }

  const response = await apiClient.get<HexagonsApiResponse | HexagonData[]>(
    '/hexagons/all',
    Object.keys(params).length > 0 ? params : undefined
  );

  if (!response.success) {
    throw new Error(response.message || `Failed to fetch hexagons`);
  }

  const data = response.data;
  const hexagons = Array.isArray(data)
    ? data
    : (data as HexagonsApiResponse)?.hexagons;

  if (!Array.isArray(hexagons)) {
    return [];
  }

  return hexagons.map((h) => ({
    ...h,
    boundary:
      (h as { boundary?: number[][] }).boundary ?? cellToBoundary(h.h3Index),
  }));
}

function getHaversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Get nearby hexagons based on latitude and longitude
 */
export async function getNearbyHexagons(
  latitude: number,
  longitude: number,
  radiusKm: number = 25,
  subcategories?: string[],
  category?: string,
  excludeFormId?: string,
  excludePartnerId?: string
): Promise<NearbyHexagon[]> {
  try {
    // Calculate bounding box with a buffer
    const deltaLat = (radiusKm * 1.2) / 111.0;
    const deltaLon = (radiusKm * 1.2) / (111.0 * Math.cos((latitude * Math.PI) / 180));
    
    const minLat = latitude - deltaLat;
    const maxLat = latitude + deltaLat;
    const minLon = longitude - deltaLon;
    const maxLon = longitude + deltaLon;

    const params: Record<string, string> = {
      minLat: minLat.toString(),
      maxLat: maxLat.toString(),
      minLon: minLon.toString(),
      maxLon: maxLon.toString(),
    };

    if (subcategories && subcategories.length > 0) {
      params.subcategories = subcategories.join(',');
    }
    if (category) {
      params.category = category;
    }
    if (excludeFormId) {
      params.excludeFormId = excludeFormId;
    }
    if (excludePartnerId) {
      params.excludePartnerId = excludePartnerId;
    }

    const response = await apiClient.get<HexagonsApiResponse | HexagonData[]>(
      '/hexagons/all',
      params
    );

    if (response.success && response.data) {
      const data = response.data;
      const hexagons = Array.isArray(data)
        ? data
        : (data as HexagonsApiResponse)?.hexagons;

      if (Array.isArray(hexagons)) {
        return hexagons
          .map((h: any) => {
            const distance = getHaversineDistance(
              latitude,
              longitude,
              h.centerLat,
              h.centerLon
            );
            return {
              h3Index: h.h3Index,
              latitude: h.centerLat,
              longitude: h.centerLon,
              distance,
              isAvailable: h.isAvailable !== false,
            };
          })
          .sort((a, b) => a.distance - b.distance)
          .slice(0, 150);
      }
    }
  } catch (error) {
    console.error('Error fetching nearby hexagons:', error);
  }
  
  return [];
}

/**
 * Get hexagon by H3 index
 */
export async function getHexagonByIndex(h3Index: string): Promise<HexagonData | null> {
  try {
    const response = await apiClient.get<HexagonData>(
      `/hexagons/${h3Index}`
    );

    if (response.success && response.data) {
      const data = response.data as HexagonData;
      // Validate required fields
      if (data.h3Index && typeof data.centerLat === 'number' &&
          typeof data.centerLon === 'number' && Array.isArray(data.boundary)) {
        return data;
      }
    }
  } catch (error) {
    console.error('Error fetching hexagon:', error);
  }

  return null;
}

/**
 * Check hexagon conflicts for given subcategories and hexagons
 */
export async function checkHexConflicts(
  subcategories: string[],
  hexagons: string[]
): Promise<{ conflicts: Array<{ h3Index: string; subcategory: string }>; unavailableHexagons: string[] }> {
  try {
    const response = await apiClient.post('/hexagons/check-conflicts', {
      subcategories,
      hexagons,
    });

    if (response.success && response.data) {
      const result = response.data as { conflicts: Array<{ h3Index: string; subcategory: string }>; unavailableHexagons: string[] };
      return result;
    }
  } catch (error) {
    console.error('Error checking hex conflicts:', error);
  }
  
  return { conflicts: [], unavailableHexagons: [] };
}

/**
 * Get hexagon statistics
 */
export async function getHexagonStats(): Promise<{
  totalHexagons: number;
  availableHexagons: number;
  claimedHexagons: number;
}> {
  try {
    const response = await apiClient.get('/hexagons/stats');

    if (response.success && response.data) {
      return response.data as { totalHexagons: number; availableHexagons: number; claimedHexagons: number };
    }
  } catch (error) {
    console.error('Error fetching hexagon stats:', error);
  }
  
  return {
    totalHexagons: 0,
    availableHexagons: 0,
    claimedHexagons: 0,
  };
}